import { useRef, useState, useEffect } from "react";

import UploadIcon from "@mui/icons-material/Upload";
import { CircularProgress } from "@mui/material";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";

import {
    AccountSectionRow,
    AccountSubtitle,
    AccountInput,
} from "@/account/StyledComponents";
import { requestSilent } from "@/Api";
import ApiAutocomplete from "@/common/ApiAutocomplete";
import ProfileAvatar from "@/common/ProfileAvatar";

export default function OrganizationDialog({
    data,
    setData,
    open,
    setError,
    handleClose,
    isSuperAdmin,
}) {
    const fileHolder = useRef();
    const [loading, setLoading] = useState(false);
    const [name, setName] = useState("");
    const [logo, setLogo] = useState(null);
    const [subscription, setSubscription] = useState(null);
    const [owner, setOwner] = useState(null);
    const [bidTemplates, setBidTemplates] = useState([]);
    const isNew = data == null;

    const handleAdd = () => {
        if (!name) {
            setError("Name is required");
            return;
        }

        setError("");
        setLoading(true);

        const formData = new FormData();
        formData.append("name", name);
        bidTemplates.forEach((id) => {
            formData.append("bid_templates_ids", id);
        });
        if (owner) {
            formData.append("owner_id", owner.account_id || owner.id);
        }
        if (subscription) {
            formData.append("subscription_id", subscription.id);
        }

        const file = fileHolder.current.files[0];
        if (file) {
            formData.append("file", file, file.name);
        }

        requestSilent
            .post(`organization`, formData)
            .then((response) => {
                setData({ ...response.data });
                handleClose();
            })
            .catch((error) => {
                setError("Failed to create workspace");
            })
            .finally(() => {
                setLoading(false);
            });
    };

    const handleUpdate = () => {
        setError("");
        setLoading(true);

        const formData = new FormData();
        formData.append("name", name);
        formData.append("owner_id", owner.account_id || owner.id);
        formData.append("subscription_id", subscription.id);

        bidTemplates.forEach((id) => {
            formData.append("bid_templates_ids", id);
        });

        const file = fileHolder.current.files[0];
        if (file) {
            formData.append("file", file, file.name);
        }

        requestSilent
            .patch(`organization/${data.id}`, formData)
            .then((response) => {
                setData({ ...response.data });
                handleClose();
            })
            .catch((error) => {
                const msg = Array.isArray(error.response.data.detail)
                    ? error.response.data.detail.map((e) => e.msg).join(", ")
                    : error.response.data.detail;
                setError(msg || "Failed to save workspace");
            })
            .finally(() => {
                setLoading(false);
            });
    };

    const handleLogo = () => {
        const file = fileHolder.current.files[0];
        setLogo({ url_orig: URL.createObjectURL(file) });
    };

    useEffect(() => {
        if (data) {
            setName(data.name);
            setLogo(data.logo);
            setOwner(data.owner);
            setSubscription(data.subscription);
        } else {
            setName("");
            setLogo(null);
        }
    }, [data]);

    return (
        <Dialog
            open={open}
            onClose={() => handleClose(false)}
            PaperProps={{
                sx: { overflowY: "visible" },
            }}
        >
            <DialogTitle>{`${isNew ? "Add" : "Edit"} Workspace`}</DialogTitle>
            <DialogContent sx={{ width: "550px", overflowY: "visible" }}>
                <AccountSectionRow>
                    <AccountSubtitle>Name</AccountSubtitle>
                    <AccountInput>
                        <TextField
                            value={name || ""}
                            onChange={(event) => setName(event.target.value)}
                            required
                            variant="filled"
                            fullWidth
                            InputProps={{
                                disableUnderline: true,
                            }}
                        />
                    </AccountInput>
                </AccountSectionRow>
                <AccountSectionRow>
                    <AccountSubtitle>Logo</AccountSubtitle>
                    <Grid container justifyContent="flex-end" spacing={4}>
                        <Grid item>
                            <ProfileAvatar image={logo} name={name} size={40} />
                        </Grid>
                        <Grid item>
                            <IconButton
                                onClick={() => fileHolder.current.click()}
                            >
                                <UploadIcon />
                            </IconButton>
                            <input
                                accept="image/png, image/jpeg, image/webp"
                                ref={fileHolder}
                                style={{ display: "none" }}
                                type="file"
                                onChange={handleLogo}
                            />
                        </Grid>
                    </Grid>
                </AccountSectionRow>

                {isSuperAdmin && (
                    <AccountSectionRow>
                        <AccountSubtitle>Subscription</AccountSubtitle>
                        <AccountInput>
                            <ApiAutocomplete
                                value={subscription}
                                apiRoute={"subscriptions"}
                                fields={["name"]}
                                isOptionEqualToValue={(option, value) =>
                                    option.id == value.id
                                }
                                onChange={(e, value) => setSubscription(value)}
                                fullOptionData
                                variant="filled"
                                fullWidth
                                InputProps={{
                                    disableUnderline: true,
                                }}
                                disableClearable
                                disabled={
                                    subscription &&
                                    subscription?.name == "Makers"
                                }
                            />
                        </AccountInput>
                    </AccountSectionRow>
                )}

                {(!isNew || isSuperAdmin) && (
                    <AccountSectionRow>
                        <AccountSubtitle>Owner</AccountSubtitle>
                        <AccountInput>
                            {isSuperAdmin ? (
                                <ApiAutocomplete
                                    value={owner}
                                    apiRoute={"admin/accounts"}
                                    onInputResponseLimit={30}
                                    fields={["name", "email"]}
                                    fieldSeperator=" - "
                                    isOptionEqualToValue={(option, value) =>
                                        option.id == value.id
                                    }
                                    onChange={(e, value) => setOwner(value)}
                                    fullOptionData
                                    variant="filled"
                                    fullWidth
                                    InputProps={{
                                        disableUnderline: true,
                                    }}
                                    disableClearable
                                />
                            ) : (
                                <ApiAutocomplete
                                    value={owner}
                                    apiRoute={"persons"}
                                    onInputResponseLimit={30}
                                    fields={["first_name", "last_name"]}
                                    isOptionEqualToValue={(option, value) =>
                                        option.id == value.id
                                    }
                                    onChange={(e, value) => setOwner(value)}
                                    fullOptionData
                                    variant="filled"
                                    fullWidth
                                    InputProps={{
                                        disableUnderline: true,
                                    }}
                                    disableClearable
                                />
                            )}
                        </AccountInput>
                    </AccountSectionRow>
                )}

                {isSuperAdmin && (
                    <AccountSectionRow>
                        <AccountSubtitle>Bid Templates</AccountSubtitle>
                        <AccountInput>
                            <ApiAutocomplete
                                value={bidTemplates}
                                apiRoute={"bid-templates"}
                                apiFilters={{ set: "all_public" }}
                                fields={["name"]}
                                onChange={(e, value) => setBidTemplates(value)}
                                multiple
                                variant="filled"
                                fullWidth
                                InputProps={{
                                    disableUnderline: true,
                                }}
                            />
                        </AccountInput>
                    </AccountSectionRow>
                )}
            </DialogContent>
            <DialogActions>
                {loading && <CircularProgress size={24} />}
                <Button
                    color="success"
                    disabled={loading}
                    onClick={isNew ? handleAdd : handleUpdate}
                >
                    Save
                </Button>
                <Button
                    color="secondary"
                    disabled={loading}
                    onClick={handleClose}
                >
                    Cancel
                </Button>
            </DialogActions>
        </Dialog>
    );
}
